import { useState, useEffect } from "react";
import { flexRender } from "@tanstack/react-table";
import * as rsuite from 'rsuite';
import { HiChevronRight, HiChevronLeft } from "react-icons/hi";

const PAGE_SIZE_OPTIONS = [
    { label: "5", value: 5 },
    { label: "10", value: 10 },
    { label: "15", value: 15 },
    { label: "25", value: 25 },
    { label: "50", value: 50 },
    { label: "همه", value: "all" },
];

// Renders a @tanstack/react-table instance as a styled, paginated HTML
// table. Columns and data stay owned by the calling page — this owns the
// table/thead/tbody markup, its visual styling, and pagination, all of
// which were previously copy-pasted (and unstyled/unpaginated) into every
// CRUD list page.
//
// Pagination is handled entirely here by slicing the already-computed rows
// rather than requiring every page to add getPaginationRowModel() to its
// own useReactTable() call — one "rows per page" control (with a "همه"
// option that turns pagination off) instead of a separate on/off toggle.
const DataTable = ({ table }) => {
    const allRows = table.getRowModel().rows;
    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(0);

    const isPaged = pageSize !== "all";
    const pageCount = isPaged ? Math.max(1, Math.ceil(allRows.length / pageSize)) : 1;

    useEffect(() => {
        if (pageIndex > pageCount - 1) {
            setPageIndex(0);
        }
    }, [pageCount, pageIndex]);

    const rows = isPaged
        ? allRows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize)
        : allRows;

    return (
        <div className="w-full h-[85%] flex flex-col">
            <div className="flex-grow rounded-lg border border-gray-200 overflow-auto bg-white">
                <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 z-10">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} className="bg-gray-50">
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-4 py-3 text-right font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap"
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={table.getAllLeafColumns().length}
                                    className="px-4 py-8 text-center text-gray-400"
                                >
                                    اطلاعاتی یافت نشد
                                </td>
                            </tr>
                        )}
                        {rows.map((row, index) => (
                            <tr
                                key={row.id}
                                className={`transition-colors hover:bg-cyan-50/60 ${index % 2 === 1 ? "bg-gray-50/50" : ""
                                    }`}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="px-4 py-3 align-middle text-gray-700">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between gap-3 py-2 px-1 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>تعداد نمایش در صفحه:</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            const value = e.target.value === "all" ? "all" : Number(e.target.value);
                            setPageSize(value);
                            setPageIndex(0);
                        }}
                        className="border border-gray-300 rounded-md text-sm px-2 py-1 bg-white text-gray-700 cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {isPaged && allRows.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <rsuite.Button
                            size="sm"
                            appearance="subtle"
                            disabled={pageIndex === 0}
                            onClick={() => setPageIndex((p) => p - 1)}
                        >
                            <HiChevronRight />
                        </rsuite.Button>
                        <span>صفحه {pageIndex + 1} از {pageCount}</span>
                        <rsuite.Button
                            size="sm"
                            appearance="subtle"
                            disabled={pageIndex >= pageCount - 1}
                            onClick={() => setPageIndex((p) => p + 1)}
                        >
                            <HiChevronLeft />
                        </rsuite.Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataTable;
